import {
  AI_RECOMPOSE_MODE,
  AI_SUGGESTION_MODE,
  getGenerationModeLabel,
  isNonEnglish,
} from 'src/data/generation';
import { GENERATION_TOOL, isMixtureTool, isMultiPromptTool } from 'src/data/generationTool';
import { PROJECT_READONLY_HINT } from 'src/data/project';
import { TEMPLATE_BUILDER_PARAMS } from 'src/data/templates';
import { createStateSelectorHook } from 'src/lib/sweetState';
import { ProjectStore, useIsProjectReadOnly } from 'src/store';
import { useMultiPromptTemplateActions } from 'src/store/multiPromptTemplates';

import { useInExtensionPromptMode, useIsGenerating } from '../../services';
import { LocalStore, useLocalActions } from '../../store';

const useProjectState = createStateSelectorHook(ProjectStore, ['selectedTov']);

const useLocalState = createStateSelectorHook(LocalStore, [
  'generationTool',
  'selectedModeId',
  'language',
  'disabledGeneration',
  'toolPrompts',
  'uploadedCsv',
  'selectedInstructionsOption',
]);

const getEnabledResult = () => ({
  disabled: false,
  hint: '',
});

const getDisabledResult = (hint = '') => ({
  disabled: true,
  hint,
});

export const useDisabledState = () => {
  const inExtensionPromptMode = useInExtensionPromptMode();
  const isProjectReadOnly = useIsProjectReadOnly();
  const generating = useIsGenerating();
  const localActions = useLocalActions();
  const multiPromptTemplateActions = useMultiPromptTemplateActions();

  const customMultiPromptId = localActions.getCustomMultiPromptId();
  const customMultiPromptTemplate = customMultiPromptId
    ? multiPromptTemplateActions.getMultiPromptTemplate(customMultiPromptId)
    : null;

  const { selectedTov } = useProjectState();

  const {
    generationTool,
    selectedModeId,
    language,
    disabledGeneration,
    toolPrompts,
    uploadedCsv,
    selectedInstructionsOption,
  } = useLocalState();

  const isCsvTemplate =
    generationTool === GENERATION_TOOL.customMultiPrompt &&
    customMultiPromptTemplate?.templateType === TEMPLATE_BUILDER_PARAMS.csv;

  if (isMultiPromptTool(generationTool)) {
    if (!toolPrompts || toolPrompts?.length === 0) {
      return getDisabledResult(
        'You haven’t completed the steps required to generate. Please Edit your brief.'
      );
    }
  }

  // We should always allow to use "generate" button in Infinity "prompt" mode
  if (inExtensionPromptMode) {
    return getEnabledResult();
  }

  // Plain generation state or we're still loading something
  if (generating || disabledGeneration) {
    return getDisabledResult();
  }

  // Project is read-only - user can't generate in it
  if (isProjectReadOnly) {
    return getDisabledResult(PROJECT_READONLY_HINT);
  }

  // "disabled" state can be set based on "language" and "generation tool" (or "sub tools" in case of a multi-prompt tool)
  const languageResult = isMultiPromptTool(generationTool)
    ? null
    : checkLanguageSupport({ generationTool, selectedModeId, language });
  if (languageResult) {
    return languageResult;
  }

  // No modes/templates to select from in the current group
  //TODO: add subgentools mode check
  if (!isMixtureTool(generationTool) && !selectedModeId) {
    return getDisabledResult();
  }

  if (!uploadedCsv && isCsvTemplate) {
    return getDisabledResult();
  }

  if (
    selectedInstructionsOption === AI_RECOMPOSE_MODE.tov &&
    selectedModeId === AI_RECOMPOSE_MODE.default &&
    !selectedTov
  ) {
    return getDisabledResult();
  }

  return getEnabledResult();
};

const checkLanguageSupport = ({ generationTool, selectedModeId, language }) => {
  // Non-english generation language selected for "rewrite" mode which does not support that
  if (
    isNonEnglish(language) &&
    [AI_RECOMPOSE_MODE.polished, AI_SUGGESTION_MODE.keywordDriven].includes(selectedModeId)
  ) {
    return getDisabledResult(
      `${getGenerationModeLabel(
        selectedModeId,
        generationTool
      )} is not supported for the selected language`
    );
  }

  return null;
};
